import { apiFetch, OfflineError } from './api';
import type { OutboxEntry } from './db';
import { cachedGet, pendingOutboxEntries, queuedMutation } from './offline';
import { authStore } from './authStore';

// ---------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------
export const authApi = {
  register: (username: string, email: string, password: string) => apiFetch<{ user: { id: string; username: string; email: string } }>('/auth/register', { method: 'POST', body: JSON.stringify({ username, email, password }) }),
  setUsername: (username: string) => apiFetch<{ user: import('./authStore').AuthUser }>('/auth/me/username', { method: 'PUT', body: JSON.stringify({ username }) }),
  login: async (email: string, password: string) => {
    const res = await apiFetch<{ tokens: { accessToken: string; refreshToken: string }; user: import('./authStore').AuthUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    authStore.setSession(res.tokens.accessToken, res.tokens.refreshToken, res.user);
    return res;
  },
  logout: async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } finally {
      authStore.clear();
    }
  },
  verifyEmail: (token: string) => apiFetch<void>('/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
  forgotPassword: (email: string) => apiFetch<void>('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token: string, newPassword: string) => apiFetch<void>('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, newPassword }) }),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<void>('/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  me: () => apiFetch<{ user: import('./authStore').AuthUser }>('/auth/me'),
  sessions: () => apiFetch<{ sessions: Array<{ id: string; userAgent: string | null; createdAt: string; expiresAt: string }>; currentSessionId: string }>('/auth/sessions'),
  revokeSession: (id: string) => apiFetch<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------
export interface GroupSummary {
  id: string;
  name: string;
  communityOptIn: boolean;
  role: 'ADMIN' | 'MEMBER';
}

export const groupsApi = {
  list: () => apiFetch<{ groups: GroupSummary[] }>('/groups'),
  create: (name: string) => apiFetch<{ group: { id: string; name: string } }>('/groups', { method: 'POST', body: JSON.stringify({ name }) }),
  get: (id: string) => apiFetch<{ group: GroupSummary & { members: Array<{ userId: string; username: string | null; email: string; role: string }> } }>(`/groups/${id}`),
  update: (id: string, data: { name?: string; communityOptIn?: boolean }) => apiFetch(`/groups/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string) => apiFetch<void>(`/groups/${id}`, { method: 'DELETE' }),
  leave: (id: string) => apiFetch<void>(`/groups/${id}/leave`, { method: 'POST' }),
  removeMember: (id: string, userId: string) => apiFetch<void>(`/groups/${id}/members/${userId}`, { method: 'DELETE' }),
  setRole: (id: string, userId: string, role: 'ADMIN' | 'MEMBER') => apiFetch<void>(`/groups/${id}/members/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
  createInvitation: (id: string) => apiFetch<{ invitation: { id: string; link: string; token: string } }>(`/groups/${id}/invitations`, { method: 'POST' }),
  listInvitations: (id: string) => apiFetch<{ invitations: Array<{ id: string; status: string; createdAt: string; link: string | null }> }>(`/groups/${id}/invitations`),
  revokeInvitation: (id: string, invitationId: string) => apiFetch<void>(`/groups/${id}/invitations/${invitationId}`, { method: 'DELETE' }),
  redeemInvitation: (token: string) => apiFetch<{ group: { id: string; name: string } }>('/invitations/redeem', { method: 'POST', body: JSON.stringify({ token }) }),
};

// ---------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------
export interface Category {
  id: string;
  name: string;
  isDefault: boolean;
  ownerScopeKey: string;
}

export const categoriesApi = {
  list: (scope: string) => apiFetch<{ categories: Category[] }>(`/categories?scope=${encodeURIComponent(scope)}`),
  create: (name: string, ownerGroupId?: string) => apiFetch<{ category: Category }>('/categories', { method: 'POST', body: JSON.stringify({ name, ownerGroupId }) }),
  rename: (id: string, name: string) => apiFetch<{ category: Category }>(`/categories/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  remove: (id: string) => apiFetch<void>(`/categories/${id}`, { method: 'DELETE' }),
};

export type UnitKind = 'COUNT' | 'PACK' | 'WEIGHT' | 'VOLUME';
export interface UnitDef {
  id?: string;
  code: string;
  label: string;
  kind: UnitKind;
  factor: number;
  allowDecimal: boolean;
}

export const unitsApi = {
  list: (scope: string) => cachedGet<{ units: UnitDef[] }>(`units:${scope}`, `/units?scope=${encodeURIComponent(scope)}`),
  create: (body: { label: string; kind: UnitKind; factor?: number; allowDecimal?: boolean; ownerGroupId?: string }) =>
    apiFetch<{ unit: UnitDef }>('/units', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: { label?: string; kind?: UnitKind; factor?: number; allowDecimal?: boolean }) =>
    apiFetch<{ unit: UnitDef }>(`/units/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => apiFetch<void>(`/units/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------
// Products & Aggregates
// ---------------------------------------------------------------------
export interface ProductSummary {
  id: string;
  name: string;
  brand: string | null;
  archived: boolean;
  categoryId: string | null;
  aggregateId: string | null;
  netQuantityValue: string | null;
  netQuantityUnit: string | null;
  notes?: string | null;
  shoppingNote?: string | null;
  preparation?: string | null;
  blocked?: boolean;
  blockNote?: string | null;
  // Standardgeschäft (Kette), in dem das Produkt in der Regel gekauft wird.
  defaultStoreChainId?: string | null;
}

export interface ExternalProduct {
  ean: string;
  name?: string;
  brand?: string;
  manufacturer?: string;
  ingredients?: string;
  allergens?: string;
  netQuantity?: string;
  images: string[];
}

export interface ProductRatings {
  ratings: Array<{ id: string; userId: string; username: string; stars: number; comment: string | null; createdAt: string; updatedAt: string }>;
  average: number | null;
  count: number;
}

export const productsApi = {
  list: (scope: string, params: { query?: string; categoryId?: string; aggregateId?: string } = {}) => {
    const qs = new URLSearchParams({ scope, ...params } as Record<string, string>);
    return apiFetch<{ products: ProductSummary[] }>(`/products?${qs}`);
  },
  get: (id: string) => apiFetch<{ product: ProductSummary & { manufacturer?: string | null; ingredients?: string | null; allergens?: string | null; barcodes: Array<{ id: string; ean: string }>; images: Array<{ id: string; url: string; isMain?: boolean }> } }>(`/products/${id}`),
  externalData: (id: string) => apiFetch<{ results: Array<{ ean: string; external: ExternalProduct | null }> }>(`/products/${id}/external-data`),
  quickCreate: (_scope: string, name: string, ownerGroupId?: string, extra: { brand?: string; categoryId?: string; aggregateId?: string } = {}) =>
    queuedMutation<{ product: ProductSummary }>({ method: 'POST', path: '/products', body: { name, ownerGroupId, ...extra }, entityType: 'Product' }),
  update: (id: string, data: Record<string, unknown>) => apiFetch<{ product: ProductSummary }>(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  archive: (id: string) => apiFetch<{ product: ProductSummary }>(`/products/${id}/archive`, { method: 'POST' }),
  addBarcode: (id: string, ean: string) => apiFetch<{ barcode: { id: string; ean: string } }>(`/products/${id}/barcodes`, { method: 'POST', body: JSON.stringify({ ean }) }),
  removeBarcode: (id: string, barcodeId: string) => apiFetch<void>(`/products/${id}/barcodes/${barcodeId}`, { method: 'DELETE' }),
  externalImage: (url: string) => apiFetch<{ dataUrl: string }>(`/products/external-image?url=${encodeURIComponent(url)}`),
  lookupByEan: (scope: string, ean: string) => apiFetch<{ found: 'local' | 'external' | 'none'; product?: ProductSummary; external?: ExternalProduct }>(`/products/barcode-lookup?scope=${encodeURIComponent(scope)}&ean=${encodeURIComponent(ean)}`),
  // SHOP-005: legt bei unbekannter EAN automatisch ein Produkt aus externen Daten an (oder liefert das lokale Produkt, falls die EAN schon bekannt ist).
  // `imageUrls`: nur diese OpenFoodFacts-Bilder übernehmen ([] = keine, undefined = alle).
  resolveByEan: (ean: string, ownerGroupId?: string, imageUrls?: string[], categoryId?: string, extra: { name?: string; aggregateId?: string } = {}) =>
    apiFetch<{ found: 'local' | 'created' | 'none'; product?: ProductSummary }>('/products/resolve-ean', { method: 'POST', body: JSON.stringify({ ean, ownerGroupId, imageUrls, categoryId, ...extra }) }),
  merge: (sourceProductId: string, targetProductId: string) => apiFetch(`/products/merge`, { method: 'POST', body: JSON.stringify({ sourceProductId, targetProductId }) }),
  assignAggregate: (id: string, aggregateId: string | null) => apiFetch<{ product: ProductSummary }>(`/products/${id}/aggregate`, { method: 'PUT', body: JSON.stringify({ aggregateId }) }),
  uploadImage: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch<{ image: { id: string; url: string } }>(`/products/${id}/images`, { method: 'POST', body: form });
  },
  ratings: (id: string) => apiFetch<ProductRatings>(`/products/${id}/ratings`),
  rate: (id: string, stars: number, comment?: string) =>
    apiFetch<ProductRatings>(`/products/${id}/rating`, { method: 'PUT', body: JSON.stringify({ stars, comment }) }),
  removeRating: (id: string) => apiFetch<void>(`/products/${id}/rating`, { method: 'DELETE' }),
  setMainImage: (id: string, imageId: string) => apiFetch(`/products/${id}/main-image`, { method: 'PUT', body: JSON.stringify({ imageId }) }),
};

export interface AggregateSummary {
  id: string;
  name: string;
  description?: string | null;
  shoppingNote?: string | null;
  categoryId?: string | null;
  category?: Category | null;
  products: ProductSummary[];
}

export interface AggregateDetail {
  id: string;
  name: string;
  description: string | null;
  shoppingNote: string | null;
  categoryId: string | null;
  category: Category | null;
  products: ProductSummary[];
  purchaseHistory: Array<{
    id: string;
    quantity: string;
    unit: string;
    unitPrice: string | null;
    totalPrice: string | null;
    isPromo: boolean;
    product: { id: string; name: string; brand: string | null };
    purchase: { id: string; purchasedAt: string; storeBranch: StoreBranch | null };
  }>;
}

export const aggregatesApi = {
  get: (id: string) => apiFetch<{ aggregate: AggregateDetail }>(`/aggregates/${id}`),
  list: (scope: string, query?: string) => apiFetch<{ aggregates: AggregateSummary[] }>(`/aggregates?scope=${encodeURIComponent(scope)}${query ? `&query=${encodeURIComponent(query)}` : ''}`),
  create: (name: string, ownerGroupId?: string) => apiFetch<{ aggregate: AggregateSummary }>('/aggregates', { method: 'POST', body: JSON.stringify({ name, ownerGroupId }) }),
  update: (id: string, body: { name?: string; description?: string | null; shoppingNote?: string | null; categoryId?: string | null }) =>
    apiFetch<{ aggregate: AggregateSummary }>(`/aggregates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  archive: (id: string) => apiFetch<void>(`/aggregates/${id}/archive`, { method: 'POST' }),
};

// ---------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------
export interface StoreBranch {
  id: string;
  name: string;
  city: string | null;
  address: string | null;
  chain: { id: string; name: string };
  listCount?: number;
  archivedAt?: string | null;
}

export interface OsmPlace {
  osmId: string;
  displayName: string;
  name?: string;
  address?: string;
  latitude: number;
  longitude: number;
  city?: string;
  postalCode?: string;
  countryCode?: string;
}

export const storesApi = {
  chains: (query = '') => apiFetch<{ chains: Array<{ id: string; name: string }> }>(`/stores/chains?query=${encodeURIComponent(query)}`),
  searchBranches: (query: string) => apiFetch<{ branches: StoreBranch[] }>(`/stores/branches?query=${encodeURIComponent(query)}`),
  usedBranches: (scope: string) => apiFetch<{ branches: StoreBranch[] }>(`/stores/branches/used?scope=${encodeURIComponent(scope)}`),
  osmSearch: (query: string) => apiFetch<{ results: OsmPlace[] }>(`/stores/branches/osm-search?query=${encodeURIComponent(query)}`),
  osmBounds: (b: { south: number; west: number; north: number; east: number }) =>
    apiFetch<{ results: OsmPlace[] }>(`/stores/branches/osm-bounds?south=${b.south}&west=${b.west}&north=${b.north}&east=${b.east}`, {
      // Overpass kann länger als das Standard-Timeout von 10 s brauchen.
      signal: AbortSignal.timeout(30000),
    }),
  createBranch: (data: Record<string, unknown>) => apiFetch<{ branch: StoreBranch }>('/stores/branches', { method: 'POST', body: JSON.stringify(data) }),
  categoryOrder: (id: string, scope: string) => apiFetch<{ order: Array<{ categoryId: string; rank: number }> }>(`/stores/branches/${id}/category-order?scope=${encodeURIComponent(scope)}`),
  setCategoryOrder: (id: string, order: Array<{ categoryId: string; rank: number }>, ownerGroupId?: string) =>
    apiFetch<void>(`/stores/branches/${id}/category-order`, { method: 'PUT', body: JSON.stringify({ order, ownerGroupId }) }),
};

// ---------------------------------------------------------------------
// Lists & Shopping mode
// ---------------------------------------------------------------------
export interface ShoppingListSummary {
  id: string;
  name: string | null;
  status: 'OPEN' | 'ARCHIVED';
  ownerUserId?: string | null;
  ownerGroupId?: string | null;
  storeBranchId: string | null;
  storeBranch: StoreBranch | null;
  _count?: { items: number };
  updatedAt: string;
  // Optionales Einkaufsdatum (ISO-Zeitstempel auf Tagesbeginn UTC, Datumsteil = "JJJJ-MM-TT").
  shoppingDate: string | null;
  // Gesetzt bei der Merkliste für den nächsten Einkauf (je Besitzbereich genau eine).
  memoScopeKey?: string | null;
}

/** "JJJJ-MM-TT"-Teil des Einkaufsdatums bzw. leer. */
export function shoppingDateInput(date: string | null | undefined): string {
  return date ? date.slice(0, 10) : '';
}

/** Einkaufsdatum für die Anzeige (z. B. "05.11.2026"). */
export function formatShoppingDate(date: string | null | undefined): string {
  return date ? date.slice(0, 10).split('-').reverse().join('.') : '';
}

export const listsApi = {
  list: (scope: string, status?: 'OPEN' | 'ARCHIVED') => cachedGet<{ lists: ShoppingListSummary[] }>(`lists:${scope}:${status ?? 'all'}`, `/lists?scope=${encodeURIComponent(scope)}${status ? `&status=${status}` : ''}`),
  create: (_scope: string, storeBranchId?: string, name?: string, ownerGroupId?: string, shoppingDate?: string) =>
    apiFetch<{ list: ShoppingListSummary }>('/lists', { method: 'POST', body: JSON.stringify({ storeBranchId, name, ownerGroupId, shoppingDate }) }),
  update: (listId: string, body: { name?: string | null; storeBranchId?: string | null; shoppingDate?: string | null }) =>
    apiFetch<{ list: ShoppingListSummary }>(`/lists/${listId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Gecachte/frische Serverdaten plus lokale, noch nicht synchronisierte Änderungen.
  detail: async (id: string) => {
    const res = await cachedGet<{ list: ListDetail }>(`list:${id}`, `/lists/${id}`);
    return { ...res, data: { list: applyPendingToList(res.data.list, await pendingOutboxEntries()) } };
  },
  addItem: (listId: string, body: { productId?: string; aggregateId?: string; freeText?: string; targetQuantity: number; unit: string }, product?: ProductSummary) =>
    queuedMutation({ method: 'POST', path: `/lists/${listId}/items`, body, entityType: 'ListItem', meta: product }),
  resetItem: (listId: string, itemId: string) => queuedMutation({ method: 'POST', path: `/lists/${listId}/items/${itemId}/reset`, entityType: 'ListItem' }),
  updateItem: (listId: string, itemId: string, body: { targetQuantity?: number; unit?: string }) =>
    apiFetch<{ item: ListItemDetail }>(`/lists/${listId}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeItem: (listId: string, itemId: string) => apiFetch<void>(`/lists/${listId}/items/${itemId}`, { method: 'DELETE' }),
  moveItem: (listId: string, itemId: string, targetListId: string) =>
    apiFetch<{ item: ListItemDetail }>(`/lists/${listId}/items/${itemId}/move`, { method: 'POST', body: JSON.stringify({ targetListId }) }),
  reopen: (listId: string) => apiFetch<{ list: ShoppingListSummary }>(`/lists/${listId}/reopen`, { method: 'POST' }),
  scan: (listId: string, ean: string) => apiFetch<ScanResult>(`/lists/${listId}/scan?ean=${encodeURIComponent(ean)}`),
  recordPurchaseItem: (listId: string, body: Record<string, unknown>) => queuedMutation({ method: 'POST', path: `/lists/${listId}/purchase-items`, body, entityType: 'PurchaseItem' }),
  markUnavailable: (listId: string, listItemId: string, quantity: number, note?: string) =>
    queuedMutation({ method: 'POST', path: `/lists/${listId}/unavailable`, body: { listItemId, quantity, note }, entityType: 'ListItem' }),
  fillMissingPrice: (listId: string, purchaseItemId: string, body: { unitPrice?: number; totalPrice?: number }) =>
    apiFetch(`/lists/${listId}/purchase-items/${purchaseItemId}/price`, { method: 'PUT', body: JSON.stringify(body) }),
  summary: async (listId: string, list?: ListDetail) => {
    try {
      return await apiFetch<{ summary: ShoppingSummary }>(`/lists/${listId}/summary`);
    } catch (err) {
      if (!(err instanceof OfflineError) || !list) throw err;
      return { summary: summarizeList(list) }; // offline: aus der (lokal ergänzten) Liste berechnen
    }
  },
  complete: (listId: string, confirmOpenItems: boolean) => apiFetch(`/lists/${listId}/complete`, { method: 'POST', body: JSON.stringify({ confirmOpenItems }) }),
  daySummary: (scope: string, date: string) => apiFetch<{ day: DaySummary }>(`/lists/day/${date}?scope=${encodeURIComponent(scope)}`),
  closeDay: (scope: string, date: string, confirmOpenLists: boolean) =>
    apiFetch<{ day: DaySummary }>('/lists/day/close', { method: 'POST', body: JSON.stringify({ scope, date, confirmOpenLists }) }),
  suggestions: (listId: string) => apiFetch<{ suggestions: ListSuggestion[] }>(`/lists/${listId}/suggestions`),
  dismissSuggestion: (listId: string, productId: string, forever: boolean) =>
    apiFetch<void>(`/lists/${listId}/suggestions/dismiss`, { method: 'POST', body: JSON.stringify({ productId, forever }) }),
  copy: (listId: string) => apiFetch<{ list: ShoppingListSummary }>(`/lists/${listId}/copy`, { method: 'POST' }),
  addDayProducts: (scope: string, date: string, products: Array<{ productId: string; targetQuantity?: number; unit?: string }>) =>
    apiFetch<{ day: DaySummary }>('/lists/day/products', { method: 'POST', body: JSON.stringify({ scope, date, products }) }),
  distributeDay: (scope: string, date: string, includeMemo: boolean) =>
    apiFetch<{ distributed: number; memoMoved: number; day: DaySummary }>('/lists/day/distribute', { method: 'POST', body: JSON.stringify({ scope, date, includeMemo }) }),
  assignDayProduct: (dayProductId: string, listId: string) =>
    apiFetch<unknown>(`/lists/day/products/${dayProductId}/assign`, { method: 'POST', body: JSON.stringify({ listId }) }),
  removeDayProduct: (dayProductId: string) => apiFetch<void>(`/lists/day/products/${dayProductId}`, { method: 'DELETE' }),
};

// Automatischer Vorschlag: regelmäßig gekauft, aber (noch) nicht auf der Liste.
export interface ListSuggestion {
  product: ProductSummary;
  targetQuantity: number;
  unit: string;
  intervalDays: number;
  purchaseCount: number;
  daysSinceLast: number;
  lastPurchasedAt: string;
}

export interface ListItemDetail {
  id: string;
  productId: string | null;
  aggregateId: string | null;
  freeText?: string | null;
  targetQuantity: string;
  unit: string;
  status: 'OPEN' | 'PARTIAL' | 'DONE' | 'UNAVAILABLE';
  fulfilledQuantity: string;
  unavailableQuantity: string;
  unavailableNote?: string | null;
  product: (ProductSummary & { category: Category | null; images?: Array<{ id: string; url: string }>; mainImageId?: string | null }) | null;
  aggregate: AggregateSummary | null;
}

type ListDetail = ShoppingListSummary & { items: ListItemDetail[]; purchase: PurchaseDetail | null };

export interface PurchaseDetail {
  id: string;
  totalAmount: string;
  items: Array<{ id: string; productId: string; quantity: string; unit: string; unitPrice: string | null; totalPrice: string | null; priceMissing: boolean; isPromo: boolean }>;
}

const PENDING_PREFIX = 'pending:';
export const isPendingId = (id: string | undefined) => !!id && id.startsWith(PENDING_PREFIX);

function itemStatus(target: number, fulfilled: number, unavailable: number): ListItemDetail['status'] {
  if (target - fulfilled - unavailable <= 0) return fulfilled > 0 ? 'DONE' : 'UNAVAILABLE';
  return fulfilled > 0 || unavailable > 0 ? 'PARTIAL' : 'OPEN';
}

// Wendet noch nicht synchronisierte Outbox-Einträge dieser Liste auf die
// angezeigten Daten an, damit lokale Änderungen offline sichtbar sind.
function applyPendingToList(list: ListDetail, entries: OutboxEntry[]): ListDetail {
  const base = `/lists/${list.id}/`;
  const mine = entries.filter((e) => e.path.startsWith(base));
  if (mine.length === 0) return list;
  const items = list.items.map((i) => ({ ...i }));
  const purchase: PurchaseDetail | null = list.purchase ? { ...list.purchase, items: [...list.purchase.items] } : null;
  let result = purchase;
  const update = (item: ListItemDetail, fulfilled: number, unavailable: number) => {
    item.fulfilledQuantity = String(fulfilled);
    item.unavailableQuantity = String(unavailable);
    item.status = itemStatus(Number(item.targetQuantity), fulfilled, unavailable);
  };
  for (const e of mine) {
    const rest = e.path.slice(base.length);
    const body = (e.body ?? {}) as Record<string, unknown>;
    if (rest === 'items') {
      const productId = body.productId as string | undefined;
      const aggregateId = body.aggregateId as string | undefined;
      const freeText = body.freeText as string | undefined;
      const quantity = Number(body.targetQuantity);
      const existing = items.find(
        (i) => i.unit === body.unit && (productId ? i.productId === productId : aggregateId ? i.aggregateId === aggregateId : i.freeText === freeText),
      );
      if (existing) {
        existing.targetQuantity = String(Number(existing.targetQuantity) + quantity);
        update(existing, Number(existing.fulfilledQuantity), Number(existing.unavailableQuantity));
      } else {
        const product = e.meta as ProductSummary | undefined;
        items.push({
          id: PENDING_PREFIX + e.id,
          productId: productId ?? null,
          aggregateId: aggregateId ?? null,
          freeText: freeText ?? null,
          targetQuantity: String(quantity),
          unit: String(body.unit),
          status: 'OPEN',
          fulfilledQuantity: '0',
          unavailableQuantity: '0',
          product: product ? { ...product, category: null } : null,
          aggregate: null,
        });
      }
    } else if (/^items\/[^/]+\/reset$/.test(rest)) {
      const item = items.find((i) => i.id === rest.split('/')[1]);
      if (item) {
        update(item, 0, 0);
        item.unavailableNote = null;
      }
    } else if (rest === 'unavailable') {
      const item = items.find((i) => i.id === body.listItemId);
      if (item) {
        update(item, Number(item.fulfilledQuantity), Number(item.unavailableQuantity) + Number(body.quantity));
        if (body.note) item.unavailableNote = String(body.note);
      }
    } else if (rest === 'purchase-items') {
      const quantity = Number(body.quantity);
      const item = items.find((i) => i.id === body.listItemId);
      if (item) update(item, Number(item.fulfilledQuantity) + quantity, Number(item.unavailableQuantity));
      const priceMissing = !!body.priceMissing;
      const totalPrice = priceMissing
        ? null
        : body.unitPrice != null
          ? Math.round(Number(body.unitPrice) * quantity * 100) / 100
          : Number(body.totalPrice);
      const entry: PurchaseDetail['items'][number] = {
        id: PENDING_PREFIX + e.id,
        productId: String(body.productId),
        quantity: String(quantity),
        unit: String(body.unit),
        unitPrice: body.unitPrice != null ? String(body.unitPrice) : totalPrice !== null && quantity > 0 ? String(totalPrice / quantity) : null,
        totalPrice: totalPrice !== null ? String(totalPrice) : null,
        priceMissing,
        isPromo: !!body.isPromo,
      };
      const current: PurchaseDetail = result ?? { id: PENDING_PREFIX + e.id, totalAmount: '0', items: [] };
      result = { ...current, totalAmount: String(Number(current.totalAmount) + (totalPrice ?? 0)), items: [...current.items, entry] };
    }
  }
  return { ...list, items, purchase: result };
}

function summarizeList(list: ListDetail): ShoppingSummary {
  const purchaseItems = list.purchase?.items ?? [];
  const missing = purchaseItems.filter((i) => i.priceMissing);
  const promoTotal = purchaseItems.filter((i) => i.isPromo).reduce((sum, i) => sum + Number(i.totalPrice ?? 0), 0);
  const total = Number(list.purchase?.totalAmount ?? 0);
  return {
    boughtCount: list.items.filter((i) => i.status === 'DONE').length,
    openCount: list.items.filter((i) => i.status === 'OPEN' || i.status === 'PARTIAL').length,
    unavailableCount: list.items.filter((i) => i.status === 'UNAVAILABLE').length,
    totalAmount: String(total),
    promoShare: total > 0 ? (Math.round((promoTotal / total) * 1000) / 10).toString() : '0',
    missingPriceCount: missing.length,
    missingPriceItems: missing,
    problemItems: list.items.flatMap((i): ProblemItem[] => {
      const fulfilled = Number(i.fulfilledQuantity);
      const unavailable = Number(i.unavailableQuantity);
      const kind: ProblemItem['kind'] | null =
        i.status === 'UNAVAILABLE' ? 'UNAVAILABLE' : unavailable > 0 || (i.status === 'PARTIAL' && fulfilled > 0) ? 'PARTIAL' : i.status === 'OPEN' || i.status === 'PARTIAL' ? 'OPEN' : null;
      if (!kind) return [];
      return [
        {
          listItemId: i.id,
          name: i.product?.name ?? i.aggregate?.name ?? 'Unbekannt',
          unit: i.unit,
          kind,
          targetQuantity: i.targetQuantity,
          fulfilledQuantity: i.fulfilledQuantity,
          unavailableQuantity: i.unavailableQuantity,
          note: i.unavailableNote ?? null,
          missingQuantity: String(Math.max(0, Number(i.targetQuantity) - fulfilled)),
        },
      ];
    }),
  };
}

/** Nicht (vollständig) erhältliche Position in der Abschlussübersicht. */
export interface ProblemItem {
  listItemId: string;
  name: string;
  unit: string;
  kind: 'UNAVAILABLE' | 'PARTIAL' | 'OPEN';
  targetQuantity: string;
  fulfilledQuantity: string;
  unavailableQuantity: string;
  note?: string | null;
  missingQuantity: string;
}

export interface DaySummary {
  scope: string;
  date: string;
  closure: { closedAt: string; closedByUserId: string | null } | null;
  openListCount: number;
  totalAmount: string;
  // Dem Tag zugeordnete Produkte; listId null = noch keiner Liste zugeordnet.
  products: Array<{
    id: string;
    productId: string;
    listId: string | null;
    targetQuantity: string;
    unit: string;
    product: { id: string; name: string; brand: string | null; defaultStoreChain: { id: string; name: string } | null };
  }>;
  lists: Array<{ list: ShoppingListSummary; summary: ShoppingSummary & { listId: string } }>;
}

export interface ShoppingSummary {
  boughtCount: number;
  openCount: number;
  unavailableCount: number;
  totalAmount: string;
  promoShare: string;
  missingPriceCount: number;
  missingPriceItems: PurchaseDetail['items'];
  problemItems: ProblemItem[];
}

export type ScanResult =
  | { status: 'UNKNOWN'; ean: string }
  | { status: 'NOT_LISTED'; product: ProductSummary }
  | { status: 'MATCHED'; product: ProductSummary; listItem: ListItemDetail }
  | { status: 'AMBIGUOUS'; product: ProductSummary; listItems: ListItemDetail[] };

// ---------------------------------------------------------------------
// Purchases & price observations
// ---------------------------------------------------------------------
export interface LastKnownPrices {
  lastPurchase: {
    id: string;
    unitPrice: string | null;
    quantity: string;
    unit: string;
    createdAt: string;
    purchase: { purchasedAt: string; storeBranch: StoreBranch | null };
  } | null;
  lastObservation: {
    id: string;
    price: string;
    observedAt: string;
    storeBranch: StoreBranch | null;
  } | null;
}

export const purchasesApi = {
  list: (scope: string) => apiFetch<{ purchases: Array<{ id: string; purchasedAt: string; totalAmount: string; storeBranch: StoreBranch | null; items: Array<{ productId: string; unitPrice: string | null }> }> }>(`/purchases?scope=${encodeURIComponent(scope)}`),
  observationsFor: (scope: string, productId: string) => apiFetch<{ observations: Array<{ id: string; price: string; observedAt: string; storeBranch: StoreBranch | null }> }>(`/price-observations?scope=${encodeURIComponent(scope)}&productId=${productId}`),
  createObservation: (body: Record<string, unknown>) => queuedMutation({ method: 'POST', path: '/price-observations', body, entityType: 'PriceObservation' }),
  lastKnown: (scope: string, productId: string) => apiFetch<LastKnownPrices>(`/price-observations/last-known/${productId}?scope=${encodeURIComponent(scope)}`),
};

// ---------------------------------------------------------------------
// Community
// ---------------------------------------------------------------------
export interface CommunityPriceEntry {
  id: string;
  productNameSnapshot: string;
  brandSnapshot: string | null;
  storeBranchId: string;
  roughArea: string | null;
  price: string;
  baseUnitPrice: string | null;
  baseUnit: string | null;
  isPromo: boolean;
  observedAt: string;
}

export const communityApi = {
  byEan: (ean: string) => apiFetch<{ prices: CommunityPriceEntry[] }>(`/community/prices?ean=${encodeURIComponent(ean)}`),
  byAggregate: (aggregateId: string) => apiFetch<{ prices: CommunityPriceEntry[] }>(`/community/prices/by-aggregate/${aggregateId}`),
  setOptIn: (scope: string, communityOptIn: boolean) => apiFetch<void>('/community/settings', { method: 'PUT', body: JSON.stringify({ scope, communityOptIn }) }),
};

// ---------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------
export const searchApi = {
  search: (scope: string, query: string) => apiFetch<{ products: ProductSummary[]; aggregates: AggregateSummary[]; categories: Category[]; storeBranches: StoreBranch[] }>(`/search?scope=${encodeURIComponent(scope)}&query=${encodeURIComponent(query)}`),
  suggestions: (scope: string) => apiFetch<{ suggestions: Array<{ product: ProductSummary; count: number }> }>(`/search/suggestions?scope=${encodeURIComponent(scope)}`),
};

// ---------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------
export interface ImportPurchaseHistoryPayload {
  userEmail?: string;
  groupId?: string;
  dryRun: boolean;
  purchases: Array<{
    purchasedAt: string;
    store: { chainName: string; branchName?: string; address?: string; city?: string; postalCode?: string };
    items: Array<{ productName: string; brand?: string; ean?: string; quantity: number; unit: string; unitPrice?: number; totalPrice?: number }>;
  }>;
}
export interface ImportSummary {
  dryRun: boolean; communityOptIn: boolean; communityPricesCreated: number; chainsCreated: number; branchesCreated: number; productsCreated: number; purchasesCreated: number; purchaseItemsCreated: number;
}

export interface AdminUserSummary {
  id: string;
  username: string | null;
  email: string;
  status: string;
  isSystemAdmin: boolean;
  createdAt: string;
}

export interface AdminUserProfile extends AdminUserSummary {
  emailVerifiedAt: string | null;
  communityOptIn: boolean;
  countryCode: string | null;
  postalCode: string | null;
  city: string | null;
  updatedAt: string;
  groupCount: number;
  activeSessions: number;
}

export interface SmtpSettings {
  configured: boolean;
  enabled?: boolean;
  host?: string;
  port?: number;
  secure?: boolean;
  allowSelfSigned?: boolean;
  username?: string;
  passwordSet?: boolean;
  fromAddress?: string;
  fromName?: string;
}

export interface SmtpSettingsPayload {
  enabled: boolean;
  host: string;
  port: number;
  secure: boolean;
  allowSelfSigned: boolean;
  username: string;
  password?: string;
  fromAddress: string;
  fromName: string;
}

export const adminApi = {
  smtp: () => apiFetch<{ smtp: SmtpSettings }>('/admin/smtp'),
  saveSmtp: (data: SmtpSettingsPayload) => apiFetch<{ smtp: SmtpSettings }>('/admin/smtp', { method: 'PUT', headers: { 'X-Admin-Confirm': 'yes' }, body: JSON.stringify(data) }),
  testSmtp: (to?: string) => apiFetch<{ ok: boolean; error?: string }>('/admin/smtp/test', { method: 'POST', body: JSON.stringify(to ? { to } : {}) }),
  users: (query?: string) => apiFetch<{ users: AdminUserSummary[] }>(`/admin/users${query ? `?query=${encodeURIComponent(query)}` : ''}`),
  user: (id: string) => apiFetch<{ user: AdminUserProfile }>(`/admin/users/${id}`),
  updateUser: (id: string, data: { username?: string; email?: string }) =>
    apiFetch<{ user: AdminUserProfile }>(`/admin/users/${id}`, { method: 'PATCH', headers: { 'X-Admin-Confirm': 'yes' }, body: JSON.stringify(data) }),
  sendPasswordReset: (id: string) => apiFetch<void>(`/admin/users/${id}/password-reset`, { method: 'POST', headers: { 'X-Admin-Confirm': 'yes' } }),
  setUserStatus: (id: string, status: 'ACTIVE' | 'SUSPENDED') => apiFetch(`/admin/users/${id}/status`, { method: 'PUT', headers: { 'X-Admin-Confirm': 'yes' }, body: JSON.stringify({ status }) }),
  groups: () => apiFetch<{ groups: Array<{ id: string; name: string; communityOptIn: boolean; _count: { memberships: number } }> }>('/admin/groups'),
  systemStatus: () => apiFetch<Record<string, unknown>>('/admin/system-status'),
  anomalies: (status?: string) => apiFetch<{ anomalies: Array<Record<string, unknown>> }>(`/admin/anomalies${status ? `?status=${status}` : ''}`),
  decideAnomaly: (id: string, decision: 'CONFIRMED' | 'REMOVED') => apiFetch(`/admin/anomalies/${id}/decide`, { method: 'POST', headers: { 'X-Admin-Confirm': 'yes' }, body: JSON.stringify({ decision }) }),
  storeBranches: (query?: string) => apiFetch<{ branches: StoreBranch[] }>(`/admin/store-branches${query ? `?query=${encodeURIComponent(query)}` : ''}`),
  setBranchArchived: (id: string, archived: boolean) => apiFetch(`/admin/store-branches/${id}/${archived ? 'archive' : 'unarchive'}`, { method: 'POST', headers: { 'X-Admin-Confirm': 'yes' } }),
  auditEvents: () => apiFetch<{ events: Array<Record<string, unknown>> }>('/admin/audit-events'),
  importPurchaseHistory: (payload: ImportPurchaseHistoryPayload) => apiFetch<{ summary: ImportSummary }>('/admin/import/purchase-history', { method: 'POST', headers: { 'X-Admin-Confirm': 'yes' }, body: JSON.stringify(payload) }),
  mergeProducts: (sourceId: string, targetId: string) => apiFetch('/admin/products/merge', { method: 'POST', headers: { 'X-Admin-Confirm': 'yes' }, body: JSON.stringify({ sourceId, targetId }) }),
};

// ---------------------------------------------------------------------
// GDPR
// ---------------------------------------------------------------------
export const gdprApi = {
  exportData: () => apiFetch<Record<string, unknown>>('/gdpr/export'),
  deleteAccount: () => apiFetch<void>('/gdpr/delete-account', { method: 'POST' }),
};

// ---------------------------------------------------------------------
// Benachrichtigungen (In-App und Web-Push)
// ---------------------------------------------------------------------
export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  url: string | null;
  readAt: string | null;
  createdAt: string;
}

export const notificationsApi = {
  list: () => apiFetch<{ notifications: AppNotification[]; unreadCount: number }>('/notifications'),
  markRead: (id: string) => apiFetch<void>(`/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () => apiFetch<void>('/notifications/read-all', { method: 'POST' }),
  pushPublicKey: () => apiFetch<{ publicKey: string | null }>('/notifications/push/public-key'),
  pushSubscribe: (subscription: PushSubscriptionJSON) => apiFetch<void>('/notifications/push/subscribe', { method: 'POST', body: JSON.stringify(subscription) }),
  pushUnsubscribe: (endpoint: string) => apiFetch<void>('/notifications/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
};
